import { List, Map, OrderedMap } from "immutable"
import { ActivityClusterBlockProps } from "../custom_components/components/activity_cluster_block"
import { Block } from "../custom_components/components/block_renderer"
import { BookProps, BooksBlockProps } from "../custom_components/components/books_block"
import { ButtonBlockProps } from "../custom_components/components/button_block"
import {
  CollectionBlockProps,
  CollectionGroup,
  CollectionGroupItem,
  CollectionGroupItemIconList,
  collectionGroupItemIconList,
} from "../custom_components/components/collection_block"
import { ContentGridBlockProps, ContentGridItem } from "../custom_components/components/content_grid_block"
import { CarouselContentGridBlockProps, CarouselContentGridItem } from "../custom_components/components/carousel_content_grid_block"
import { FaqBlockProps } from "../custom_components/components/faq_block"
import { FeaturedContentBlockProps } from "../custom_components/components/featured_content_block"
import { HighlightBlockProps } from "../custom_components/components/highlight_block"
import { ImagesBlockProps } from "../custom_components/components/images_block"
import { ImageBlockProps } from "../custom_components/components/image_block"
import { RichTextBlockProps } from "../custom_components/components/rich_text_block"
import { SubscriptionsBlockProps } from "../custom_components/components/subscriptions_block"
import { VacancyOverviewBlockProps } from "../custom_components/components/vacancy_overview_block"
import { VideoBlockProps } from "../custom_components/components/video_block"
import {
  ParsedParagraphActivityCluster,
  ParsedParagraphBooks,
  ParsedParagraphCollection,
  ParsedParagraphContentGrid,
  ParsedParagraphFaqCollection,
  ParsedParagraphFeaturedContent,
  ParsedParagraphHighlight,
  ParsedParagraphImages,
  ParsedParagraphInlineMedia,
  ParsedParagraphKnop,
  ParsedParagraphMedia,
  ParsedParagraphText,
  ParsedParagraphVacancyOverview,
  ParsedParagraphPageGrid,
  ParsedParagraphQuote,
  ParsedParagraphCaroussel,
  ParsedParagraphCollectionSearchBar,
  ParsedParagraphSubscriptionBlock,
  ParsedParagraphGridBlock,
  ParsedParagraphTippedActivitiesBlock,
  ParsedParagraphWordcloudBlock,
  ParsedParagraphFormstackBlock,
  ParsedParagraphCtaBlock,
  ParsedParagraphNewsletterSignupForm,
  ParsedParagraphSubmenu,
  ParsedParagraphThemakiezerEmbed
} from "../../Shared/drupal_models/types"
import { getTeasers } from "./api"
import { AsyncBlockProps } from "../custom_components/components/async_block"
import { Teaser } from "../custom_models/drupal"
import { fixMediaUrl, processRichText } from "./drupal_node_conversion"
import { QuoteBlockProps } from "../custom_components/components/quote_block"
import { CollectionSearchBarProps } from "../custom_components/components/collection_search_bar"
import { GridBlockProps, GridItem } from "../custom_components/components/grid_block"
import { TippedActivitiesBlockProps } from "../custom_components/components/tipped_activities_block"
import { WordCloudBlockProps } from "../custom_components/components/word_cloud_block"
import { FormstackBlockProps } from "../custom_components/components/formstack/formstack_block"
import { loadingAsyncState } from "widgets-for-react"
import { loadFormstackForm } from "../custom_components/components/formstack/formstack_api"
import { CtaBlockProps } from "../custom_components/components/cta_block"
import { NewsletterFormBlockProps } from "../custom_components/components/newsletterFormBlock/newsletterFormBlockState"
import { QuickLink } from "../custom_components/components/quick_links"
import { SubMenuProps } from "../custom_components/components/submenu_block"
import { slugify } from "../custom_utils/helpers"
import { ThemakiezerBlockProps } from "../custom_components/components/ThemakiezerBlock/ThemakiezerBlockState"

export const richTextBlock: (p: ParsedParagraphText) => RichTextBlockProps = p => ({
  kind: "richText",
  title: p.field_title,
  html: p.field_rich_text ? processRichText(p.field_rich_text) : "",
  primaryActionTitle: p.field_primary_action ? p.field_primary_action.title : "",
  primaryActionUrl: p.field_primary_action ? p.field_primary_action.url : "",
  secondaryActionTitle: p.field_secondary_action ? p.field_secondary_action.title : "",
  secondaryActionUrl: p.field_secondary_action ? p.field_secondary_action.url : "",
  media: p.field_inline_media
    ? {
        media: mediaBlock(p.field_inline_media),
        position: p.field_inline_media.field_position == "text_left" ? "text_left" : "text_right",
      }
    : "none",
})

export type MediaBlockProps = VideoBlockProps & { kind: "video" } | ImageBlockProps & { kind: "image" }
export const mediaBlock: (p: ParsedParagraphMedia | ParsedParagraphInlineMedia) => MediaBlockProps = p => {
  return p.field_media.bundle == "video"
    ? {
        kind: "video",
        videoUrl: p.field_media.field_media_video_embed_field.iframe_url,
        videoName: p.field_media.label,
        autoplay: false,
        controls: true,
        info: true,
      }
    : {
        kind: "image",
        imageUrl: fixMediaUrl(p.field_media.field_media_image.image.style_urls["790w"]),
        imageAlt: p.field_media.field_media_image.alt,
      }
}

export const faqBlock: (p: ParsedParagraphFaqCollection) => FaqBlockProps = p => ({
  kind: "faq",
  title: p.field_faq_title,
  entries: p.field_faq_item.reduce(
    (a, e) => a.set(e.label, e.field_answer ? processRichText(e.field_answer) : ""),
    OrderedMap<string, string>()
  ),
})

export const highlightBlock: (p: ParsedParagraphHighlight) => HighlightBlockProps = p => ({
  kind: "highlight",
  title: p.field_title,
  subtitle: p.field_subtitle,
  content: p.field_rich_text,
})

export const tippedActivitiesBlock: (p: ParsedParagraphTippedActivitiesBlock) => TippedActivitiesBlockProps = p => ({
  kind: "tipped_activities",
  title: p.field_title,
  content: p.field_rich_text,
  linkTitle: p.field_link.title,
  linkUrl: p.field_link.url,
  row_count: p.field_row_count ? p.field_row_count : 3
})

export const buttonBlock: (p: ParsedParagraphKnop) => ButtonBlockProps = p => ({
  kind: "knop",
  caption: p.field_link.title,
  href: p.field_link.url,
})

export const imagesBlock: (p: ParsedParagraphImages) => ImagesBlockProps = p => ({
  kind: "images",
  images: p.field_images.reduce(
    (l, i) =>
      l.push({
        imageAlt: i.field_media_image.alt,
        imageUrl: fixMediaUrl(i.field_media_image.image.style_urls["790x445"]),
      }),
    List<ImageBlockProps>()
  ),
})

export const vacancyOverviewBlock: (p: ParsedParagraphVacancyOverview) => VacancyOverviewBlockProps = p => ({
  kind: "vacancy_overview",
  title: p.field_title,
  html: p.field_rich_text,
  placeholder: p.field_placeholder_text,
  category_id: p.field_category.id,
  row_count: p.field_row_count ? p.field_row_count : 3,
})

export const booksOverviewBlock: (p: ParsedParagraphBooks) => BooksBlockProps = p => ({
  kind: "books",
  books: p.field_books
    .map<BookProps>(b => ({
      title: b.field_title,
      author: b.field_author,
      imageUrl: fixMediaUrl(b.field_image.field_media_image.image.style_urls["380h"]),
      imageAlt: b.field_image.field_media_image.alt,
      url: b.field_link.url
    }))
    .toList(),
  title: p.field_title,
  text: p.field_text,
})

export const themakiezerBlock: (p: ParsedParagraphThemakiezerEmbed) => ThemakiezerBlockProps = p => ({
  kind: "themakiezer",
  embedId: p.field_themakiezer_embed_id,
  title: p.field_title,
  text: p.field_text,
})

export const collectionBlock: (p: ParsedParagraphCollection) => CollectionBlockProps = p => ({
  kind: "collection",
  title: p.field_title,
  introduction: p.field_rich_text,
  displayQuickLinks: p.field_display_quick_links,
  collectionGroups: p.field_collection_groups
    .map<CollectionGroup>(group => ({
      title: group.field_title,
      slug: slugify(group.field_title),
      collectionItems: group.field_collection_items
        .map<CollectionGroupItem>(item => {
          let icon: CollectionGroupItemIconList = collectionGroupItemIconList
            .filter(v => v === item.field_icon)
            .toList()
          return {
            title: item.field_title,
            subtitle: item.field_subtitle,
            text: item.field_text,
            link: item.field_link.url,
            icon: !icon.isEmpty() ? icon.first() : "none",
          }
        })
        .toList(),
    }))
    .toList(),
})

export const featuredContentBlock: (p: ParsedParagraphFeaturedContent) => FeaturedContentBlockProps = p => ({
  kind: "featured_content",
  title: p.field_title,
  html: p.field_rich_text,
  imageUrl: fixMediaUrl(p.field_image.field_media_image.image.style_urls["790w"]),
  imageAlt: p.field_image.field_media_image.alt,
  linkTitle: p.field_link.title,
  linkUrl: p.field_link.url,
})

export const activityClusterBlock: (p: ParsedParagraphActivityCluster) => ActivityClusterBlockProps = p => ({
  kind: "activity_cluster",
  title: p.field_title,
  description: p.field_text,
  slug: "",
  activitySummariesIds: p.field_product_db_ids,
  row_count: p.field_row_count ? p.field_row_count : 3
})

export const subscriptionsBlock: (p: ParsedParagraphSubscriptionBlock) => SubscriptionsBlockProps = p => ({
  kind: "subscription_block",
})

export const contentGridBlock: (p: ParsedParagraphContentGrid) => ContentGridBlockProps = p => ({
  kind: "content_grid",
  row_count: p.field_row_count ? p.field_row_count : 3,
  items: p.field_content_grid_items.reduce(
    (l, i) =>
      l.push({
        title: i.field_title,
        subtitle: i.field_subtitle ? i.field_subtitle : "",
        label: i.field_label ? i.field_label : "",
        imageAlt: i.bundle == "content_grid_item" ? i.field_image.field_media_image.alt : "",
        imageUrl: i.bundle == "content_grid_item" ? fixMediaUrl(i.field_image.field_media_image.image.style_urls["790x445"]) : "",
        videoUrl: i.bundle == "content_grid_video" ? i.field_video.iframe_url : "",
        linkUrl: i.field_link ? i.field_link.url : ""
      }),
    List<ContentGridItem>()
  ),
})

export const carouselContentGridBlock: (p : ParsedParagraphCaroussel) => CarouselContentGridBlockProps = p => ({ 
  kind: "carousel_content_grid",
  title: p.field_title,
  row_count: p.field_row_count ? p.field_row_count : 3,
  items: p.field_content_grid_items.reduce(
    (l, i) =>
      l.push({
        title: i.field_title,
        subtitle: i.field_subtitle ? i.field_subtitle : "",
        label: i.field_label ? i.field_label : "",
        imageAlt: i.bundle == "content_grid_item" ? i.field_image.field_media_image.alt : "",
        imageUrl: i.bundle == "content_grid_item" ? fixMediaUrl(i.field_image.field_media_image.image.style_urls["790x445"]) : "",
        videoUrl: i.bundle == "content_grid_video" ? i.field_video.iframe_url : "",
        linkUrl: i.field_link.url
      }),
    List<CarouselContentGridItem>()
  ),
  primaryActionTitle: p.field_primary_action ? p.field_primary_action.title : "",
  primaryActionUrl: p.field_primary_action ? p.field_primary_action.url : "",
})

export const pageGridBlock: (p: ParsedParagraphPageGrid) => AsyncBlockProps<Map<string, Teaser>> = p => ({
  kind: "async_block",
  promise: () => getTeasers(p.field_page_grid_items.map(i => i.id.toString()).toArray()),
  block: teasers => ({
    kind: "content_grid",
    title: p.field_title,
    row_count: p.field_row_count ? p.field_row_count : 3,
    items: teasers.map<ContentGridItem>(t => ({
      title: t.title,
      linkUrl: t.url,
      imageAlt: t.imageAlt,
      imageUrl: t.imageUrl,
      subtitle: t.description,
    })).toList(),
    primaryActionTitle: p.field_primary_action ? p.field_primary_action.title : undefined,
    primaryActionUrl: p.field_primary_action ? p.field_primary_action.url : undefined,
  })
})

export const quoteBlock: (p: ParsedParagraphQuote) => QuoteBlockProps = p => {
  return ({
    kind: "quote",
    quote: p.field_quote_text,
    source: p.field_quote_source && p.field_quote_source != "" ? p.field_quote_source : undefined,
  })
}

export const collectionSearchBarBlock: (p: ParsedParagraphCollectionSearchBar) => CollectionSearchBarProps = p => {
  return ({
    kind: "collection_search_bar"
  })
}

export const gridBlock: (p: ParsedParagraphGridBlock) => GridBlockProps = p => {
  return ({
    kind: "grid_block",
    row_count: p.field_row_count ? p.field_row_count : 3,
    items: p.field_grid_items.reduce(
      (l, i) =>
        l.push({
          title: i.field_title,
          imageAlt: i.field_media.field_media_image.alt,
          imageUrl: i.field_media.field_media_image.image.url,
          intro: i.field_text,
          linkUrl: i.field_link.url
        }),
      List<GridItem>()
    )
  })
}
export const formstackBlock: (p: ParsedParagraphFormstackBlock) => FormstackBlockProps = p => {
  return ({
    kind: p.bundle,
    formId: p.field_formstack_form_id,
    state: loadingAsyncState(() => loadFormstackForm(p.field_formstack_form_id))
  })
}

export const wordCloudBlock: (p: ParsedParagraphWordcloudBlock) => WordCloudBlockProps = p => {
  return ({
    kind: "word_cloud_block",
    title: p.field_title,
    description: p.field_description,
    label: p.field_label,
    form_id: p.field_formstack_form_id,
    field_id: p.field_formstack_field_id,
    number_of_options: p.field_number_of_options,
    primaryActionTitle: p.field_primary_action?.title,
    primaryActionUrl: p.field_primary_action?.url
  })
}

export const ctaBlock: (p: ParsedParagraphCtaBlock) => CtaBlockProps = p => {
  return ({
    kind: "cta_block",
    title: p.field_title,
    description: p.field_description,
    primaryActionTitle: p.field_primary_action ? p.field_primary_action.title : "",
    primaryActionUrl: p.field_primary_action ? p.field_primary_action.url : "",
    secondaryActionTitle: p.field_secondary_action ? p.field_secondary_action.title : "",
    secondaryActionUrl: p.field_secondary_action ? p.field_secondary_action.url : ""
  })
}

export const subMenuBlock: (p: ParsedParagraphSubmenu) => SubMenuProps = p => {
  return ({
    kind: "submenu",
    title: p.field_title,
    text: p.field_text,
    links: p.field_items.reduce(
      (l, i) =>
        l.push({
          title: i.field_link.title,
          url:
            i.field_link.url.startsWith('http') ? decodeURIComponent(i.field_link.url) :  // Ensure query params survive the trip from Drupal to React
            i.field_link.url.startsWith('#') ? '#' + slugify(i.field_link.url) :          // Convert any text into a pretty URL fragment
            i.field_link.url                                                              // Probably an internal link; leave it be
        }),
      List<QuickLink>()
    )
  })
}

export const newsletterSignupBlock: (p: ParsedParagraphNewsletterSignupForm) => NewsletterFormBlockProps = _ => ({ kind: 'newsletterForm' })

export const emptyBlock: () => Block = () => ({ kind: "empty" })
